import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Loader2, Sparkles } from "lucide-react";
import { GFFrame, GFHeader } from "@/components/GFFrame";
import { aiListCampaigns, aiGetOwner, aiCreativeUrl } from "@/lib/gfApi";
import { CreditChip, UpsellModal, GrowthContextCard, CAMPAIGN_STATUS, readAiEmail } from "@/pages/ai/aiShared";
import { BrandKitCard } from "@/pages/ai/BrandKitCard";

export default function Campaigns() {
  const navigate = useNavigate();
  const [email] = useState(readAiEmail());
  const [items, setItems] = useState(null);
  const [credits, setCredits] = useState(null);
  const [owner, setOwner] = useState(null);
  const [upsell, setUpsell] = useState(false);
  const [inputEmail, setInputEmail] = useState("");

  const load = async (e) => {
    if (!e) return;
    try {
      const [list, ow] = await Promise.all([aiListCampaigns(e), aiGetOwner(e)]);
      setItems(list.campaigns || []);
      setOwner(ow);
      setCredits(ow.credits);
    } catch (err) {
      toast.error("Gagal memuat kempen.");
      setItems([]);
    }
  };

  useEffect(() => { if (email) load(email); }, [email]);

  const startNew = () => {
    if (!owner?.active_assessment_id) {
      toast.error("Tiada laporan Business Growth aktif ditemui untuk emel ini.");
      return;
    }
    // Early upsell: a 0-credit user is offered Growth+/top-up before entering the wizard.
    if ((credits?.usable ?? 0) < 1) {
      setUpsell(true);
      return;
    }
    navigate(`/campaigns/new?assessment_id=${owner.active_assessment_id}&email=${encodeURIComponent(email)}`);
  };

  if (!email) {
    return (
      <GFFrame testId="campaigns-page">
        <div className="flex min-h-screen flex-col px-6 pb-10">
          <GFHeader />
          <div className="gf-gold-line my-5" />
          <h2 className="font-heading text-2xl font-bold text-[#0F172A]">Kempen Saya</h2>
          <p className="mt-2 text-sm text-slate-500">Masukkan emel yang anda gunakan untuk laporan Growth Factor.</p>
          <input
            data-testid="campaigns-email-input"
            className="mt-4 w-full rounded-2xl border border-[#E7E2D6] px-4 py-3.5 text-[15px] outline-none focus:border-[#D4AF37]"
            placeholder="emel@contoh.com" value={inputEmail}
            onChange={(e) => setInputEmail(e.target.value)}
          />
          <button
            data-testid="campaigns-email-submit"
            onClick={() => { const v = inputEmail.trim(); if (v) { localStorage.setItem("gf_ai_email", v); window.location.href = `/campaigns?email=${encodeURIComponent(v)}`; } }}
            className="mt-4 rounded-full bg-[#D4AF37] py-3.5 text-sm font-bold text-[#0F172A]"
          >
            Lihat Kempen
          </button>
        </div>
      </GFFrame>
    );
  }

  return (
    <GFFrame testId="campaigns-page">
      <div className="min-h-screen pb-16">
        <div className="bg-[#0F172A] px-6 pb-7 pt-9 text-white">
          <button onClick={() => navigate(-1)} data-testid="campaigns-back-btn" className="flex items-center gap-1 text-sm text-slate-400">
            <ArrowLeft className="h-4 w-4" /> Kembali
          </button>
          <div className="mt-3 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[#D4AF37]" />
            <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#D4AF37]">Brand2Market AI</span>
          </div>
          <h1 className="mt-2 font-heading text-3xl font-bold text-white">Kempen Saya</h1>
          <div className="mt-4 flex items-center justify-between">
            {credits ? <CreditChip credits={credits} /> : <span />}
            <button data-testid="campaigns-add-credits-btn" onClick={() => setUpsell(true)} className="text-xs font-semibold text-[#D4AF37] underline">
              Tambah kredit
            </button>
          </div>
        </div>

        <div className="px-6 pt-5">
          {owner?.diagnosis_context && (
            <div className="mb-4">
              <GrowthContextCard ctx={owner.diagnosis_context}
                onReview={() => navigate(`/report/${owner.active_assessment_id}`)} />
            </div>
          )}
          <BrandKitCard email={email} onChange={() => load(email)} />
          <button
            data-testid="new-campaign-btn"
            onClick={startNew}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-[#D4AF37] py-3.5 text-sm font-bold text-[#0F172A] transition-transform active:scale-[0.98]"
          >
            <Plus className="h-4 w-4" /> Kempen Baharu
          </button>
          {credits && credits.usable === 0 && (
            <p className="mt-2 text-center text-xs text-slate-400" data-testid="zero-credit-note">
              Kredit habis? Anda masih boleh melihat kempen lengkap; beli Growth+ / top-up untuk kempen baharu.
            </p>
          )}
        </div>

        <div className="mt-6 space-y-3 px-6" data-testid="campaigns-list">
          {items === null ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#D4AF37]" /></div>
          ) : items.length === 0 ? (
            <p className="py-10 text-center text-sm text-slate-400">Belum ada kempen. Cipta kempen pertama anda.</p>
          ) : (
            items.map((c) => {
              const st = CAMPAIGN_STATUS[c.status] || CAMPAIGN_STATUS.draft;
              return (
                <button
                  key={c.id}
                  data-testid={`campaign-card-${c.id}`}
                  onClick={() => navigate(`/campaigns/${c.id}`)}
                  className="w-full rounded-2xl border border-[#E7E2D6] bg-white p-4 text-left transition-colors hover:border-[#D4AF37]"
                >
                  <div className="flex items-start gap-3">
                    {c.status === "completed" && c.has_creative && (
                      <img
                        data-testid={`campaign-thumb-${c.id}`}
                        src={aiCreativeUrl(c.id)}
                        alt=""
                        className="h-16 w-16 shrink-0 rounded-xl border border-[#E7E2D6] object-cover"
                      />
                    )}
                    <div className="flex min-w-0 flex-1 items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate font-heading text-base font-bold text-[#0F172A]">{c.custom_name || c.business_name}</p>
                        <p className="mt-0.5 line-clamp-2 text-sm text-slate-500">{c.objective}</p>
                      </div>
                      <span data-testid="campaign-status-badge" className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold ${st.cls}`}>
                        {st.label}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>
      <UpsellModal open={upsell} onClose={() => { setUpsell(false); load(email); }}
        email={email} assessmentId={owner?.active_assessment_id || ""}
        zeroState={credits?.usable === 0} />
    </GFFrame>
  );
}
